import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Tool } from '@/types/database';
import { Skeleton } from '@/components/ui/skeleton';
import { trackHome } from '@/lib/homeAnalytics';
import { cn } from '@/lib/utils';
import { ToolLogo, PRICING_LABEL } from './ToolMark';

function priceText(t: Tool) {
  const base = PRICING_LABEL[t.pricing] ?? null;
  if (t.starting_price && t.starting_price > 0) return `${base ? base + ' · ' : ''}from $${t.starting_price}`;
  return base;
}

function FeatureCard({ tool, large }: { tool: Tool; large?: boolean }) {
  const price = priceText(tool);
  const useCase = tool.best_for?.[0];
  return (
    <Link
      to={`/tool/${tool.slug}`}
      onClick={() => trackHome('featured_tool_opened', tool.slug)}
      className={cn(
        'group flex flex-col rounded-2xl border border-border/60 bg-card p-5 transition-all hover:border-primary/50 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        large && 'md:col-span-2 md:p-7'
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <ToolLogo tool={tool} className={large ? 'h-14 w-14 text-xl' : 'h-11 w-11'} />
        {price && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{price}</span>
        )}
      </div>
      <h3 className={cn('font-display font-semibold group-hover:text-primary transition-colors', large ? 'text-2xl' : 'text-lg')}>
        {tool.name}
      </h3>
      <p className={cn('text-muted-foreground mt-2 flex-1', large ? 'text-base line-clamp-3' : 'text-sm line-clamp-2')}>
        {tool.short_description}
      </p>
      <div className="mt-4 flex items-center justify-between gap-3 text-sm">
        {useCase ? <span className="text-primary truncate">Best for {useCase.toLowerCase()}</span> : <span />}
        <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" aria-hidden />
      </div>
    </Link>
  );
}

export function ToolsWorthExploring({ tools, isLoading }: { tools: Tool[]; isLoading: boolean }) {
  if (!isLoading && tools.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-14 md:py-16">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-2">Featured tools</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Worth exploring.</h2>
          </div>
          <Link to="/tools" className="hidden sm:inline-flex items-center text-sm font-medium text-primary hover:underline">
            Browse all tools <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className={cn('h-48 rounded-2xl', i < 2 && 'md:col-span-2')} />
              ))
            : tools.map((t, i) => <FeatureCard key={t.id} tool={t} large={i < 2} />)}
        </div>
        <Link to="/tools" className="sm:hidden mt-6 inline-flex items-center text-sm font-medium text-primary">
          Browse all tools <ArrowRight className="h-4 w-4 ml-1" />
        </Link>
      </div>
    </section>
  );
}
